/**
 * Formatação de valores no padrão brasileiro (PT-BR)
 * - Vírgula (,) como separador decimal
 * - Ponto (.) como separador de milhar
 * - Exemplo: 1.000,50
 */

/**
 * Formata um valor como moeda brasileira (R$)
 * @param {number|string|null|undefined} value - Valor a ser formatado
 * @param {boolean} showSymbol - Se deve mostrar o símbolo R$ (default: true)
 * @returns {string} Valor formatado no padrão PT-BR
 */
export const formatCurrency = (value, showSymbol = true) => {
  if (value === null || value === undefined) {
    return showSymbol ? 'R$ 0,00' : '0,00';
  }
  
  const num = Number(value);
  if (isNaN(num)) {
    return showSymbol ? 'R$ 0,00' : '0,00';
  }
  
  if (showSymbol) {
    return num.toLocaleString('pt-BR', { 
      style: 'currency', 
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  
  return num.toLocaleString('pt-BR', { 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Formata um valor de moeda completo (sem abreviações K/M/B)
 * Sempre mostra o valor completo no padrão PT-BR
 * @param {number|string|null|undefined} value - Valor a ser formatado
 * @returns {string} Valor formatado completo (ex: R$ 1.234.567,89)
 */
export const formatCurrencyCompact = (value) => {
  // Formato completo PT-BR - sem abreviações
  return formatCurrency(value, true);
};

/**
 * Formata um número decimal no padrão PT-BR (sem símbolo de moeda)
 * @param {number|string|null|undefined} value - Valor a ser formatado
 * @param {number} decimals - Número de casas decimais (default: 2)
 * @returns {string} Número formatado no padrão PT-BR
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return '0,00';
  
  const num = Number(value);
  if (isNaN(num)) return '0,00';
  
  return num.toLocaleString('pt-BR', { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formata um valor como porcentagem no padrão PT-BR
 * @param {number|string|null|undefined} value - Valor a ser formatado
 * @param {number} decimals - Número de casas decimais (default: 1)
 * @returns {string} Porcentagem formatada no padrão PT-BR
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0,0%';
  
  const num = Number(value);
  if (isNaN(num)) return '0,0%';
  
  return `${num.toLocaleString('pt-BR', { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}%`;
};

/**
 * Formata uma data no padrão PT-BR
 * @param {string|Date} date - Data a ser formatada
 * @param {boolean} includeTime - Se deve incluir horário (default: false)
 * @returns {string} Data formatada no padrão PT-BR
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  if (includeTime) {
    return d.toLocaleString('pt-BR');
  }
  
  return d.toLocaleDateString('pt-BR');
};

/**
 * Converte uma string de moeda PT-BR para número
 * @param {string} value - Valor no formato "R$ 1.234,56" ou "1.234,56"
 * @returns {number} Valor numérico
 */
export const parseCurrency = (value) => {
  if (!value) return 0;
  
  // Remove R$, espaços e pontos (separador de milhar)
  // Substitui vírgula por ponto (separador decimal)
  const cleaned = value
    .toString()
    .replace(/R\$\s?/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();
  
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

export default {
  formatCurrency,
  formatCurrencyCompact,
  formatNumber,
  formatPercent,
  formatDate,
  parseCurrency
};
